import React, { useCallback, useEffect, useMemo, useState } from "react";

import LoadingSpinner from "../LoadingSpinner";
import Modal from "../Modal";
import { Button } from "../ui/button";
import { errMsg } from "../../lib/errMsg";
import {
  MANUAL_FIELD_TYPES,
  type ManualFieldDef,
  type ManualFieldType,
  type ManualProfileCompany,
  type ManualProfileDetail,
  type ManualProfileSummary,
  createManualProfile,
  fetchManualProfile,
  fetchManualProfiles,
  setCompanyManualProfile,
  updateManualProfile,
} from "../../services/manualProfilesApi";
import { useAuth } from "../../contexts/AuthContext";

const inputCls = "w-full border border-secondary-300 rounded px-2 py-1 text-sm";
const smallInput = "border border-secondary-300 rounded px-2 py-1 text-sm";

function blankField(): ManualFieldDef {
  return { key: "", label: "", type: "text", required: false };
}

/** Editor for one field list (mc_fields or mi_fields). */
const FieldListEditor: React.FC<{
  title: string;
  fields: ManualFieldDef[];
  onChange: (next: ManualFieldDef[]) => void;
}> = ({ title, fields, onChange }) => {
  const patch = (i: number, p: Partial<ManualFieldDef>) =>
    onChange(fields.map((f, idx) => (idx === i ? { ...f, ...p } : f)));
  const remove = (i: number) => onChange(fields.filter((_, idx) => idx !== i));
  const move = (i: number, d: -1 | 1) => {
    const j = i + d;
    if (j < 0 || j >= fields.length) return;
    const next = fields.slice();
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };

  return (
    <div className="border border-secondary-200 rounded p-3">
      <div className="flex items-center justify-between mb-2">
        <h4 className="font-semibold text-sm text-secondary-700">{title}</h4>
        <Button variant="outline" size="sm" onClick={() => onChange([...fields, blankField()])}>
          + Add field
        </Button>
      </div>
      {fields.length === 0 && (
        <div className="text-xs text-secondary-400">No fields.</div>
      )}
      <div className="space-y-2">
        {fields.map((f, i) => (
          <div key={i} className="border border-secondary-100 rounded p-2 bg-secondary-50">
            <div className="flex flex-wrap gap-2 items-center">
              <input
                className={`${smallInput} w-40`}
                placeholder="label"
                value={f.label}
                onChange={(e) => patch(i, { label: e.target.value })}
              />
              <input
                className={`${smallInput} w-32 font-mono`}
                placeholder="key"
                value={f.key}
                onChange={(e) => patch(i, { key: e.target.value })}
              />
              <select
                className={smallInput}
                value={f.type}
                onChange={(e) => patch(i, { type: e.target.value as ManualFieldType })}
              >
                {MANUAL_FIELD_TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
              <label className="flex items-center gap-1 text-xs text-secondary-600">
                <input
                  type="checkbox"
                  checked={f.required}
                  onChange={(e) => patch(i, { required: e.target.checked })}
                />
                required
              </label>
              <label className="flex items-center gap-1 text-xs text-secondary-600">
                span
                <input
                  type="number"
                  min={1}
                  max={6}
                  className={`${smallInput} w-14`}
                  value={f.column_span ?? ""}
                  onChange={(e) =>
                    patch(i, {
                      column_span: e.target.value ? Number(e.target.value) : null,
                    })
                  }
                />
              </label>
              <div className="ml-auto flex gap-1">
                <button type="button" title="Up" className="text-xs px-1 text-secondary-500 hover:text-secondary-800" onClick={() => move(i, -1)}>▲</button>
                <button type="button" title="Down" className="text-xs px-1 text-secondary-500 hover:text-secondary-800" onClick={() => move(i, 1)}>▼</button>
                <button type="button" title="Remove" className="text-xs px-1 text-error-600 hover:text-error-800" onClick={() => remove(i)}>🗑</button>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              <input
                className={`${smallInput} flex-1 min-w-[180px]`}
                placeholder="placeholder (optional)"
                value={f.placeholder ?? ""}
                onChange={(e) => patch(i, { placeholder: e.target.value || null })}
              />
              {f.type === "select" && (
                <textarea
                  className={`${smallInput} flex-1 min-w-[180px]`}
                  rows={2}
                  placeholder="select options, one per line"
                  value={(f.select_options ?? []).join("\n")}
                  onChange={(e) =>
                    patch(i, {
                      select_options: e.target.value
                        .split("\n")
                        .map((s) => s.trim())
                        .filter(Boolean),
                    })
                  }
                />
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

interface EditorState {
  profileId: number | null; // null = creating
  profileKey: string;
  displayName: string;
  mc: ManualFieldDef[];
  mi: ManualFieldDef[];
}

/**
 * Manual-checkout PROFILE editor (platform-only). Edit the FORM FIELDS a
 * customer's manual checkout / check-in screens render, and assign companies to
 * a profile. Enablement is per-smartbox (elsewhere); this is fields only.
 */
export const ManualProfilesTab: React.FC = () => {
  const { token, role } = useAuth();
  const isPlatform = role === "platform";

  const [profiles, setProfiles] = useState<ManualProfileSummary[] | null>(null);
  const [companies, setCompanies] = useState<ManualProfileCompany[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [editor, setEditor] = useState<EditorState | null>(null);

  const reload = useCallback(async () => {
    if (!token) return;
    const d = await fetchManualProfiles(token);
    setProfiles(d.profiles);
    setCompanies(d.companies);
  }, [token]);

  useEffect(() => {
    if (!isPlatform || !token) return;
    fetchManualProfiles(token)
      .then((d) => {
        setProfiles(d.profiles);
        setCompanies(d.companies);
      })
      .catch((e) => setError(errMsg(e)));
  }, [token, isPlatform]);

  const profileName = useMemo(() => {
    const m = new Map<number, string>();
    (profiles ?? []).forEach((p) => m.set(p.profile_id, p.display_name));
    return m;
  }, [profiles]);

  if (!isPlatform) {
    return <div className="text-sm text-secondary-500">Platform admins only.</div>;
  }

  const openCreate = () =>
    setEditor({ profileId: null, profileKey: "", displayName: "", mc: [], mi: [] });

  const openEdit = async (id: number) => {
    if (!token) return;
    setError(null);
    try {
      const d: ManualProfileDetail = await fetchManualProfile(token, id);
      setEditor({
        profileId: d.profile_id,
        profileKey: d.profile_key,
        displayName: d.display_name,
        mc: d.mc_fields,
        mi: d.mi_fields,
      });
    } catch (e) {
      setError(errMsg(e));
    }
  };

  const save = async () => {
    if (!token || !editor) return;
    setBusy(true);
    setError(null);
    try {
      if (editor.profileId === null) {
        await createManualProfile(token, {
          profile_key: editor.profileKey.trim(),
          display_name: editor.displayName.trim(),
          mc_fields: editor.mc,
          mi_fields: editor.mi,
        });
      } else {
        await updateManualProfile(token, editor.profileId, {
          display_name: editor.displayName.trim(),
          mc_fields: editor.mc,
          mi_fields: editor.mi,
        });
      }
      setEditor(null);
      await reload();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const assign = async (companyId: number, profileId: number | null) => {
    if (!token) return;
    setBusy(true);
    setError(null);
    try {
      await setCompanyManualProfile(token, companyId, profileId);
      await reload();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      {error && (
        <div className="text-xs text-error-700 bg-error-50 px-2 py-1 rounded">{error}</div>
      )}

      <section>
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold text-sm uppercase tracking-wide text-secondary-700">
            Manual checkout profiles
          </h3>
          <Button size="sm" onClick={openCreate}>+ New profile</Button>
        </div>
        {profiles === null && <LoadingSpinner label="loading profiles" />}
        {profiles && (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-secondary-500">
                <th className="py-1">Name</th>
                <th>Key</th>
                <th>Checkout fields</th>
                <th>Checkin fields</th>
                <th>Companies</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {profiles.map((p) => (
                <tr key={p.profile_id} className="border-t border-secondary-100">
                  <td className="py-1">{p.display_name}</td>
                  <td className="font-mono text-xs text-secondary-500">{p.profile_key}</td>
                  <td>{p.mc_field_count}</td>
                  <td>{p.mi_field_count}</td>
                  <td>{p.company_count}</td>
                  <td className="text-right">
                    <Button variant="outline" size="sm" onClick={() => void openEdit(p.profile_id)}>
                      Edit
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section>
        <h3 className="font-semibold text-sm uppercase tracking-wide text-secondary-700 mb-2">
          Company assignments
        </h3>
        <div className="text-[11px] text-secondary-500 mb-2">
          Which profile each company's manual checkout/checkin forms use.
        </div>
        <table className="w-full text-sm">
          <tbody>
            {companies.map((c) => (
              <tr key={c.company_id} className="border-t border-secondary-100">
                <td className="py-1">
                  {c.company_name}
                  <span className="text-xs text-secondary-400 ml-1">({c.company_type})</span>
                </td>
                <td className="text-right">
                  <select
                    className={smallInput}
                    disabled={busy}
                    value={c.manual_checkout_profile_id ?? ""}
                    onChange={(e) =>
                      void assign(c.company_id, e.target.value ? Number(e.target.value) : null)
                    }
                  >
                    <option value="">— none —</option>
                    {(profiles ?? []).map((p) => (
                      <option key={p.profile_id} value={p.profile_id}>
                        {profileName.get(p.profile_id) ?? p.display_name}
                      </option>
                    ))}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {editor && (
        <Modal
          open
          onClose={() => setEditor(null)}
          title={editor.profileId === null ? "New profile" : "Edit profile"}
          widthClass="max-w-2xl"
        >
          <div className="space-y-3">
            {editor.profileId === null && (
              <label className="block">
                <span className="block text-xs uppercase tracking-wide text-secondary-500 mb-1">
                  Profile key (stable id, e.g. acme_field_form)
                </span>
                <input
                  className={`${inputCls} font-mono`}
                  value={editor.profileKey}
                  onChange={(e) => setEditor({ ...editor, profileKey: e.target.value })}
                />
              </label>
            )}
            <label className="block">
              <span className="block text-xs uppercase tracking-wide text-secondary-500 mb-1">
                Display name
              </span>
              <input
                className={inputCls}
                value={editor.displayName}
                onChange={(e) => setEditor({ ...editor, displayName: e.target.value })}
              />
            </label>
            <FieldListEditor
              title="Checkout fields (MC)"
              fields={editor.mc}
              onChange={(mc) => setEditor({ ...editor, mc })}
            />
            <FieldListEditor
              title="Check-in fields (MI)"
              fields={editor.mi}
              onChange={(mi) => setEditor({ ...editor, mi })}
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setEditor(null)}>Cancel</Button>
              <Button size="sm" disabled={busy} onClick={() => void save()}>
                {busy ? "Saving…" : "Save"}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default ManualProfilesTab;
